import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, GripVertical, Sun, Moon, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

export default function RoutineCustomizer({ profile, setProfile, defaultMorning, defaultNight, onClose }) {
  const [morning, setMorning] = useState(profile?.morning_items || defaultMorning);
  const [night, setNight] = useState(profile?.night_items || defaultNight);
  const [newLabel, setNewLabel] = useState("");
  const [newSection, setNewSection] = useState("morning");
  const [saving, setSaving] = useState(false);

  const removeItem = (section, id) => {
    if (section === "morning") setMorning(morning.filter((i) => i.id !== id));
    else setNight(night.filter((i) => i.id !== id));
  };

  const addItem = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    const id = `c_${Date.now()}`;
    const item = { id, label: trimmed, desc: "Custom step" };
    if (newSection === "morning") setMorning([...morning, item]);
    else setNight([...night, item]);
    setNewLabel("");
  };

  const reset = () => {
    setMorning(defaultMorning);
    setNight(defaultNight);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateProfile({
        morning_items: morning,
        night_items: night,
      });
      setProfile(updated);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const Section = ({ title, icon: Icon, items, section, color }) => (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} strokeWidth={1.8} />
        <h4 className="font-heading text-sm font-medium text-[#2A332C]">{title}</h4>
      </div>
      <div className="space-y-1.5">
        {items.map((it) => (
          <div
            key={it.id}
            className="flex items-center gap-2 p-2.5 bg-white border border-[#E9EFEA] rounded-xl"
          >
            <GripVertical className="w-4 h-4 text-[#6B7A70]" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#2A332C] truncate">{it.label}</div>
              <div className="text-[11px] text-[#6B7A70] truncate">{it.desc}</div>
            </div>
            <button
              data-testid={`remove-${section}-${it.id}`}
              onClick={() => removeItem(section, it.id)}
              className="w-7 h-7 rounded-full bg-[#FADBD8]/40 flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5 text-[#CC7A6B]" strokeWidth={2} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-[#6B7A70] italic px-2">No steps yet — add one below.</div>
        )}
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          className="bg-[#F7F5F0] w-full max-w-md rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="routine-customizer"
        >
          <div className="sticky top-0 bg-[#F7F5F0] px-6 pt-5 pb-3 flex items-center justify-between border-b border-[#E9EFEA] z-10">
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#2A332C]">
                Customize routine
              </h3>
              <p className="text-xs text-[#6B7A70]">Add, remove or rename steps.</p>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-white border border-[#E9EFEA] flex items-center justify-center" data-testid="close-customizer">
              <X className="w-4 h-4" strokeWidth={1.8} />
            </button>
          </div>

          <div className="px-6 py-4">
            <Section title="Morning" icon={Sun} items={morning} section="morning" color="#E6B97A" />
            <Section title="Night" icon={Moon} items={night} section="night" color="#4A6B53" />

            <div className="p-3 bg-white rounded-2xl border border-[#E9EFEA]">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#6B7A70] mb-2">
                Add a step
              </div>
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => setNewSection("morning")}
                  className={`flex-1 py-1.5 text-xs rounded-full ${
                    newSection === "morning"
                      ? "bg-[#E9F0E8] text-[#4A6B53] font-semibold"
                      : "bg-[#F7F5F0] text-[#6B7A70]"
                  }`}
                >
                  Morning
                </button>
                <button
                  onClick={() => setNewSection("night")}
                  className={`flex-1 py-1.5 text-xs rounded-full ${
                    newSection === "night"
                      ? "bg-[#E9F0E8] text-[#4A6B53] font-semibold"
                      : "bg-[#F7F5F0] text-[#6B7A70]"
                  }`}
                >
                  Night
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="new-item-input"
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addItem()}
                  placeholder="e.g. Niacinamide serum"
                  className="flex-1 px-3 py-2 rounded-xl border border-[#E9EFEA] text-sm bg-[#F7F5F0]"
                />
                <button
                  data-testid="add-item-btn"
                  onClick={addItem}
                  className="px-3 py-2 bg-[#2A332C] text-white rounded-xl text-sm font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} />
                  Add
                </button>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-[#F7F5F0]/95 backdrop-blur border-t border-[#E9EFEA] px-6 py-4 flex gap-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <button
              onClick={reset}
              data-testid="reset-routine"
              className="px-4 py-3 bg-white border border-[#E9EFEA] rounded-full text-sm font-medium flex items-center gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.8} />
              Reset
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-routine"
              className="flex-1 py-3 bg-[#2A332C] text-white rounded-full text-sm font-medium disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save routine"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
